<template>
  <div class="partner-resumes-base-resumes">
    <div v-if="pending && !receivedData"
         style="min-height: 20rem; position: relative">
      <BasePreloader/>
    </div>
    <ErrorView v-if="error"
               :code="error.code"
               :title="error.message"/>

    <template v-else-if="receivedData">
      <div class="partner-resumes-base-resumes__table">
        <APResumesBaseResumePageHeadRow :is-empty="isEmpty"/>
        
        <div class="partner-resumes-base-resumes__table-content">
          <div v-if="isEmpty" class="partner-resumes-base-resumes__empty">
            <picture class="partner-resumes-base-resumes__empty-bg">
              <!--              <source srcset="@/assets/img/empty-resume-base.svg" media="(max-width: 550px)">-->
              <img src="@/assets/img/empty-resume-base.svg" alt="bg">
            </picture>
            <h3 class="partner-resumes-base-resumes__empty-title">{{
                $tResumesBaseResumes('Resume database is empty')
              }}</h3>
            <div class="partner-resumes-base-resumes__empty-text">
              {{ $tResumesBaseResumes('To get started, invite the first candidate') }}
            </div>
            <div class="partner-resumes-base-resumes__empty-btn-wrap">
              <button @click.prevent="$emit('invite-employee')"
                      class="partner-resumes-base-resumes__empty-btn  base-btn  base-btn--green">
                <BaseSvg name="ico-plus"
                         width="1.1rem"
                         height="1.1rem"/>
                <span>{{ $tResumesBaseResumes('Invite a candidate') }}</span>
              </button>
              <router-link-modified v-if="isPartner || isAgent"
                                    tag="button"
                                    class="partner-resumes-base-resumes__empty-btn base-btn  base-btn--green"
                                    to="/account/register-candidate">
                <BaseSvg name="ico-plus"
                         width="1.1rem"
                         height="1.1rem"/>
                <span>{{ $tResumesBaseResumes('Register a candidate') }}</span>
              </router-link-modified>
            </div>
          </div>

          <template v-else>
            <template v-for="item in receivedData.data">
              <APResumesBaseResumePageRow :key="item.id"
                                          :resume="item"
                                          :ref="`resume-${item.id}`"
                                          @resend="setResend"/>
            </template>
          </template>

          <BasePreloader v-if="pending"/>
        </div>
      </div>

      <div class="partner-resumes-base-resumes__footer">
        <Pagination v-if="receivedData.last_page > 1" :quantity="receivedData.last_page"
                    :withGoTo="false"
                    class="partner-resumes-base-resumes__pagination"/>

        <div :class="`partner-resumes-base-resumes__total ${receivedData.last_page <= 1 ? '__sorted' : ''}`">
          <h2>{{$tResumesBaseResumes('total')}}: {{receivedData.total}}</h2>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
  import {http} from '@/axios'
  import {CancelToken, isCancel} from 'axios'
  import handleError from '@/helpers/handleError'
  import ErrorView from '@/components/ErrorView'
  import Pagination from '@/components/Pagination'
  import APResumesBaseResumePageHeadRow from '@/components/partnerResumeBaseResumes/APResumesBaseResumePageHeadRow'
  import APResumesBaseResumePageRow from '@/components/partnerResumeBaseResumes/APResumesBaseResumePageRow'
  import {mapGetters} from 'vuex'

  const filterConstants = {
    FILTER_KEY_CANDIDATE: 'f-candidate',
    FILTER_KEY_REFERRAL: 'f-referral',
    FILTER_KEY_STATUS: 'f-status',
    FILTER_KEY_RANGE: 'f-range',
    PAGE: 'page',
  }

  export default {
    name: 'AccountPartnerResumesBaseResumesPage',
    components: {
      ErrorView,
      Pagination,
      APResumesBaseResumePageHeadRow,
      APResumesBaseResumePageRow,
    },
    provide: {
      ...filterConstants
    },
    data() {
      return {
        pending: null,
        receivedData: null,
        error: null,
        cancel: null,
        storage: JSON.parse(localStorage.getItem('resentLinks')) || [],
        ...filterConstants
      }
    },
    computed: {
      ...mapGetters('user', [
        'isModerator',
        'isPartner',
        'isAgent',
      ]),
      isEmpty() {
        return !this.pending && !this.$get(this.receivedData, 'data', []).length
      },
    },
    methods: {
      load() {
        if (this.cancel) {
          this.cancel()
          this.cancel = null
        }

        this.pending = true
        this.error = null

        const query = this.$route.query
        const params = {}

        for (const key in filterConstants) {
          const paramKey = filterConstants[key]

          params[paramKey] = query[paramKey]
        }

        const link = this.isModerator
          ? '/api/backoffice/resume-base'
          : '/api/cabinet/agent/resume-base'

        http.get(link, {
          params,
          cancelToken: new CancelToken((c) => {
            this.cancel = c;
          })
        })
          .then(({data}) => {
            this.receivedData = data
            this.pending = false
          })
          .catch(e => {
            if (isCancel(e)) {
              return
            }

            this.receivedData = null

            this.error = {
              code: e.response && e.response.status,
              message: handleError(e)
            }

            this.pending = false
          })
      },
      initResend() {
        if (this.storage.length) {
          for (const [key, resume] of this.storage.entries()) {
            if (resume.time + 8640000 < new Date().getTime()) {
              this.storage.splice(key, 1)
            }
          }
        }
        localStorage.setItem('resentLinks', JSON.stringify(this.storage));
      },
      setResend(id) {
        let resume = this.receivedData.data.find(item => item.id == id)
        if (resume.statuses[0].active) {
          if (this.storage.length == 0) {
            this.storage.push({id: resume.id, time: new Date().getTime()})
          } else {
            let noSuchRecord = !this.storage.filter(record => record.id == resume.id).length
            if (noSuchRecord) {
              this.storage.push({id: resume.id, time: new Date().getTime()})
            }
          }
        }
        localStorage.setItem('resentLinks', JSON.stringify(this.storage));
        this.$refs[`resume-${id}`][0].checkResend()
      }
    },
    created() {
      this.load()
      this.initResend()

      createMapRouteQueryWatchers(
        this,
        Object.values(filterConstants),
        () => {
          this.load()
        }
      )

      
    }
  }

  function createMapRouteQueryWatchers(vm, keys, callBack) {
    keys.forEach(key => {
      vm.$watch(
        function () {
          return vm.$route.query[key]
        },
        callBack
      )
    })
  }
</script>

<style lang="less">

</style>
